import { Badge, Box, chakra, Flex, Image, Text } from '@chakra-ui/react';
import deadlineIcon from 'assets/img/selfIpo/deadline.svg';
import minimumAmountIcon from 'assets/img/selfIpo/minimum-amount.svg';
import priceRangeIcon from 'assets/img/selfIpo/price-range.svg';
import AppCard from 'presentation/components/shared/card/AppCard';
import TagsRow, { TagSize } from 'presentation/components/shared/TagsRow';
import { Color } from 'presentation/theme/colorScheme';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import SelfIpoParamRow from './SelfIpoParamRow';
import {
  formatSelfIpoDateTime,
  formatSelfIpoMoneyAmount,
} from './selfIpoUtils';

interface Props {
  offering: SelfIpoOffering;
  onClick: () => void;
}

const SelfIpoOfferingCard: FC<Props> = ({ offering, onClick, ...props }) => {
  const { i18n, t } = useTranslation();

  return (
    <AppCard
      overflow="hidden"
      cursor="pointer"
      onClick={onClick}
      display="flex"
      flexDir={{ base: 'column', md: 'row' }}
      h="100%"
      minH={{ base: 'auto', md: '300px' }}
      {...props}
    >
      <Box
        h={{ base: '220px', md: '100%' }}
        minH={{ md: '300px' }}
        w={{ base: '100%', md: '42%' }}
        bgColor={Color.Shade100}
        position="relative"
        flexShrink={0}
      >
        {offering.coverImageUrl && (
          <Image
            src={offering.coverImageUrl}
            alt={offering.companyName}
            objectFit="cover"
            w="100%"
            h="100%"
          />
        )}
        <Badge
          position="absolute"
          top="12px"
          right="12px"
          colorScheme={offering.isAccepting ? 'green' : 'gray'}
          borderRadius="12px"
          px="10px"
          py="4px"
          textTransform="uppercase"
        >
          {offering.isAccepting
            ? t('selfIpoOpenForApplications')
            : t('selfIpoClosedForApplications')}
        </Badge>
      </Box>
      <Flex p={{ base: '18px', md: '20px' }} flex="1" flexDir="column">
        <Box flex="1">
          <Badge
            colorScheme="green"
            borderRadius="10px"
            px="10px"
            py="4px"
            textTransform="uppercase"
            mb="8px"
          >
            {offering.tickerCode}
          </Badge>
          <Text fontWeight={700} fontSize="24px" noOfLines={2} lineHeight="1.2">
            {offering.companyName}
          </Text>
          <Text mt="4px" color={Color.Shade600} noOfLines={1}>
            {offering.sector}
          </Text>
          <TagsRow
            mt="10px"
            tags={offering.tags.slice(0, 3)}
            size={TagSize.small}
            tagColor={Color.Primary500}
          />
        </Box>
        <Box mt="15px">
          <SelfIpoParamRow
            iconSrc={priceRangeIcon}
            label={t('selfIpoPriceRange')}
          >
            <Text>
              ${formatSelfIpoMoneyAmount(offering.priceLow)} - $
              {formatSelfIpoMoneyAmount(offering.priceHigh)}
            </Text>
          </SelfIpoParamRow>
          <SelfIpoParamRow
            iconSrc={minimumAmountIcon}
            label={t('selfIpoMinAmount')}
          >
            <Text>${formatSelfIpoMoneyAmount(offering.minAmount)}</Text>
          </SelfIpoParamRow>
          <SelfIpoParamRow
            iconSrc={deadlineIcon}
            label={t('selfIpoAcceptanceDeadline')}
            showBorder={false}
          >
            <Text>
              {formatSelfIpoDateTime(
                offering.acceptanceDeadline,
                i18n.language
              )}
            </Text>
          </SelfIpoParamRow>
        </Box>
      </Flex>
    </AppCard>
  );
};

export default chakra(SelfIpoOfferingCard);
